/**
 * PlayerMachine — pure state machine for the proposal previewer.
 *
 * Owns every transition between the high-level player states:
 *
 *   `closed`         → modal not visible
 *   `intro`          → cinematic intro overlay is up (heart-pulse + name reveal)
 *   `playing`        → slide deck auto-advancing
 *   `paused`         → user paused via the controls / space / P
 *   `respond`        → DATE finale dialog (proposer -> Yes / propose-new-time / restart)
 *
 * No React imports. No JSX. No side-effects. The hook layer
 * (`usePlayerMachine`) is the only thing that knows how to translate
 * these transitions into React state.
 *
 * Why a state machine:
 *   1. Hides ~80 LOC of imperative setState plumbing inside the modal
 *   2. Easy to unit-test in isolation (just send/receive events)
 *   3. Decouples side-effects (timers, focus) from the state itself
 *
 * Note: this mirrors the legacy `ProposalPreview` behaviour 1:1 so the
 * visual output and DATE respond flow don't change.
 */
import type { PreviewSlideData } from '../PreviewSlide';

export type ProposalType = 'MARRIAGE' | 'RELATIONSHIP' | 'DATE';

export type PlayerPhase = 'closed' | 'intro' | 'playing' | 'paused' | 'respond' | 'done';

export interface PlayerState {
  phase: PlayerPhase;
  /** Index into `slides`. `0` is the cinematic intro phase; `1..N` map
   *  to `slides[i-1]` so the same indices keep working through the
   *  intro→deck transition. */
  index: number;
  /** Snapshot of the slide being left. Used by the page-transition
   *  stage to render the OLD layer during advance. */
  prevSlide: PreviewSlideData | null;
  /** Direction of the last advance: 1 forward, -1 back. Drives both
   *  framer-motion and CSS-keyframe variants of the page transition. */
  direction: 1 | -1;
  /** Monotonic counter. Bumped on every advance so the slide layer
   *  remounts and re-runs entry animations. */
  playbackKey: number;
  /** Beat-by-beat phase of the cinematic intro: 0..4 (heart → hint →
   *  "A moment for" → name scale-in → Begin button live). */
  introBeat: number;
  /** True while the top-level curtain is animating. Lets the modal
   *  hide its controls until the panels have parted. */
  curtainPending: boolean;
}

export type PlayerEvent =
  | { type: 'OPEN'; slides: PreviewSlideData[] }
  | { type: 'CLOSE' }
  | { type: 'BEGIN' }
  | { type: 'NEXT' }
  | { type: 'PREV' }
  | { type: 'RESTART' }
  | { type: 'TOGGLE_PLAY' }
  | { type: 'AUTO_ADVANCE_TICK' }
  | { type: 'OPEN_RESPOND' }
  | { type: 'CLOSE_RESPOND' }
  | { type: 'INTRO_TICK'; beat: number }
  | { type: 'CURTAIN_DONE' }
  | { type: 'CONTENT_CHANGED' }
  | { type: 'TRANSITION_CHANGED' };

/* ─── pure helpers ──────────────────────────────────────────────── */

function clampIndex(slides: PreviewSlideData[], index: number): number {
  if (slides.length === 0) return 0;
  return Math.max(0, Math.min(index, slides.length));
}

function defaultCurtainDurationMs(openingAnimation: string): number {
  // Non-curtain openings skip the wait entirely; curtain needs ~5s of
  // panel animation + 0.3s collapse. We expose the constant so the
  // hook can drive the curtain-done timer without a magic number.
  if (openingAnimation === 'cinematic-curtain') return 5300;
  return 0;
}

/* ─── reducer ───────────────────────────────────────────────────── */

export function createInitialState(): PlayerState {
  return {
    phase: 'closed',
    index: 0,
    prevSlide: null,
    direction: 1,
    playbackKey: 0,
    introBeat: 0,
    curtainPending: false,
  };
}

/**
 * Derive the next state given the current state + an event.
 *
 * The hook layer is responsible for actually persisting the state and
 * for the side-effects (timers, focus) — this function does no I/O.
 */
export function reducePlayer(
  state: PlayerState,
  event: PlayerEvent,
  ctx: { slides: PreviewSlideData[]; openingAnimation: string },
): PlayerState {
  switch (event.type) {
    case 'OPEN':
      return {
        ...createInitialState(),
        phase: 'intro',
        playbackKey: state.playbackKey + 1,
        curtainPending: defaultCurtainDurationMs(ctx.openingAnimation) > 0,
      };

    case 'CLOSE':
      return { ...createInitialState() };

    case 'BEGIN':
      if (state.phase !== 'intro') return state;
      // Auto-begin on curtain-completion lands here with `index === 0`,
      // but the auto-advance scheduler and `currentSlide = slides[index-1]`
      // both require `index >= 1`. Advance to slide 0 so the deck
      // renders the first card AND the auto-advance timer fires
      // (otherwise the player is stuck until the user presses →).
      return {
        ...state,
        phase: 'playing',
        index: Math.max(1, state.index),
        introBeat: 0,
        playbackKey: state.playbackKey + 1,
      };

    case 'NEXT': {
      if (ctx.slides.length === 0) return state;
      const current = clampIndex(ctx.slides, state.index - 1);
      if (current >= ctx.slides.length - 1) {
        return { ...state, phase: 'done' };
      }
      return {
        ...state,
        phase: 'playing',
        prevSlide: ctx.slides[current] ?? null,
        direction: 1,
        index: state.index + 1,
        playbackKey: state.playbackKey + 1,
      };
    }

    case 'PREV': {
      if (ctx.slides.length === 0 || state.index <= 1) return state;
      const current = clampIndex(ctx.slides, state.index - 1);
      return {
        ...state,
        phase: 'playing',
        prevSlide: ctx.slides[current] ?? null,
        direction: -1,
        index: state.index - 1,
        playbackKey: state.playbackKey + 1,
      };
    }

    case 'RESTART':
      return {
        ...createInitialState(),
        phase: 'intro',
        playbackKey: state.playbackKey + 1,
        curtainPending: defaultCurtainDurationMs(ctx.openingAnimation) > 0,
      };

    case 'TOGGLE_PLAY':
      if (state.phase === 'playing') return { ...state, phase: 'paused' };
      if (state.phase === 'paused') return { ...state, phase: 'playing' };
      return state;

    case 'AUTO_ADVANCE_TICK': {
      if (state.phase !== 'playing' || ctx.slides.length === 0) return state;
      if (state.index < 1) return state;
      const slideIdx = state.index - 1;
      const slide = ctx.slides[slideIdx];
      if (!slide) return state;
      // `enableAutoAdvance === false` is honored by the scheduler
      // (it just doesn't fire this event). We still no-op here in
      // case the timer was already in-flight when the toggle flipped.
      if (slide.enableAutoAdvance === false) return state;
      if ((slide.duration ?? 0) <= 0) return state;
      const current = clampIndex(ctx.slides, state.index - 1);
      if (current >= ctx.slides.length - 1) {
        // Last slide; the hook decides between respond-dialog vs finale.
        // DATE is handled by the hook (it dispatches OPEN_RESPOND); for
        // every other proposal type we transition into `done` so the
        // FinaleOverlay can show.
        return { ...state, phase: 'done' };
      }
      return {
        ...state,
        prevSlide: ctx.slides[current] ?? null,
        direction: 1,
        index: state.index + 1,
        playbackKey: state.playbackKey + 1,
      };
    }

    case 'OPEN_RESPOND':
      if (state.phase === 'intro') return state;
      return { ...state, phase: 'respond' };

    case 'CLOSE_RESPOND':
      if (state.phase !== 'respond') return state;
      return { ...state, phase: 'paused' };

    case 'INTRO_TICK': {
      if (state.phase !== 'intro') return state;
      if (event.beat === state.introBeat) return state;
      return { ...state, introBeat: event.beat };
    }

    case 'CURTAIN_DONE':
      if (!state.curtainPending) return state;
      return { ...state, curtainPending: false };

    case 'CONTENT_CHANGED':
    case 'TRANSITION_CHANGED':
      // Editing content/transitions while open bumps playbackKey so the
      // stage remounts with the new metadata — but does NOT reset the
      // playhead. Index, direction, prevSlide stay put.
      return { ...state, playbackKey: state.playbackKey + 1 };
  }
}

/** External scheduler signals that should resolve to player events. */
export const PlayerScheduler = {
  curtainDoneMs(openingAnimation: string): number {
    return defaultCurtainDurationMs(openingAnimation);
  },
};
